import type {
  AttachmentMeta,
  CommentFragment,
  RichDoc,
  TaskDetailData,
} from "@/lib/board";
import { STOPLIGHT } from "@/lib/stoplight";

import { describeAction } from "./format";

/**
 * The detail rail is split: Activity (the sign-off lifecycle events) on top,
 * then a divider, then the Comments thread (ClickUp + Greenlight comments, one
 * level of threaded replies). The "request changes" feedback renders in its own
 * section beneath the body, not inline in this timeline.
 */
export type ActivityComment = {
  id: string; // render key
  refId: string; // reply target — a ClickUp comment id or a Greenlight uuid
  commentId: string | null; // raw Greenlight comment id (null for ClickUp comments → not editable)
  authorId: string | null; // for gating edit-your-own
  editedAt: string | null;
  date: string | null;
  authorName: string | null;
  authorAvatar: string | null;
  authorInitials: string | null;
  text: string;
  fragments: CommentFragment[]; // ClickUp-origin rich fragments (empty for Greenlight)
  rich: RichDoc | null; // Greenlight rich doc (null for ClickUp-origin)
  attachments: AttachmentMeta[]; // Greenlight attachments (empty for ClickUp-origin)
};
export type CommentNode = ActivityComment & { replies: ActivityComment[] };

// A decision echo (the accept 🟢 sign-off) is recorded as a ledger comment so a
// failed ClickUp push is retried, but it's represented in the timeline by its
// audit event — never as a discussion comment. Drop the un-pushed native echo
// here (the pushed one's ClickUp copy is already stripped server-side by
// dropGreenlightEchoes). Case-insensitive to match the tag's legacy casing too.
const GREENLIGHT_ECHO_TAG = "[greenlight:";
const isDecisionEcho = (body: string): boolean =>
  body.toLowerCase().startsWith(GREENLIGHT_ECHO_TAG);
export type ActivityEvent = {
  id: string;
  date: string | null;
  text: string;
  action: string;
  toStatus: string | null; // resulting sign-off state — drives the dot color
};

// Dot colors match the stoplight palette used on the board / status indicators.
export const SIGNOFF_DOT: Record<string, string> = {
  pending_review: STOPLIGHT.amber,
  changes_requested: STOPLIGHT.red,
  accepted: STOPLIGHT.green,
};

export function buildActivity(detail: TaskDetailData): {
  events: ActivityEvent[];
  comments: CommentNode[];
} {
  const ms = (d: string | null) => (d ? new Date(d).getTime() : 0);
  const byDate = (a: { date: string | null }, b: { date: string | null }) =>
    ms(a.date) - ms(b.date);

  const events: ActivityEvent[] = detail.history
    .map((h) => ({
      id: `h-${h.id}`,
      date: h.createdAt,
      text: describeAction(h),
      action: h.action,
      toStatus: h.toStatus,
    }))
    .sort(byDate);

  // Flatten both comment origins. A PUSHED general comment shows from ClickUp's
  // thread (its source of truth — so a ClickUp deletion removes it here too); the
  // server already drops our tagged decision echoes. Native rows are shown only
  // for UN-pushed comments (not yet synced to ClickUp). Feedback lives in events.
  type Flat = ActivityComment & { parentRef: string | null };
  const flat: Flat[] = [
    ...detail.clickupComments.map((c) => ({
      id: `c-${c.id}`,
      refId: c.id,
      commentId: null, // ClickUp's own comment — not editable here
      authorId: null,
      editedAt: null,
      parentRef: null,
      date: c.date,
      authorName: c.authorName,
      authorAvatar: c.authorAvatar,
      authorInitials: c.authorInitials,
      text: c.text,
      fragments: c.fragments,
      rich: null,
      attachments: [] as AttachmentMeta[],
    })),
    ...detail.comments
      .filter(
        (c) =>
          c.kind === "comment" && !c.pushedToClickup && !isDecisionEcho(c.body),
      )
      .map((c) => ({
        id: `g-${c.id}`,
        refId: c.id,
        commentId: c.id,
        authorId: c.authorId,
        editedAt: c.editedAt,
        parentRef: c.parentId ?? null,
        date: c.createdAt,
        authorName: c.authorName,
        authorAvatar: c.authorAvatar,
        authorInitials: null,
        text: c.body,
        fragments: [] as CommentFragment[],
        rich: c.rich,
        attachments: c.attachments,
      })),
  ];

  // Group replies under their parent; orphans (parent off-page) become top-level.
  const refs = new Set(flat.map((f) => f.refId));
  const repliesByParent = new Map<string, Flat[]>();
  const tops: Flat[] = [];
  for (const f of flat) {
    if (f.parentRef && refs.has(f.parentRef)) {
      const arr = repliesByParent.get(f.parentRef) ?? [];
      arr.push(f);
      repliesByParent.set(f.parentRef, arr);
    } else {
      tops.push(f);
    }
  }

  const strip = ({ parentRef: _p, ...c }: Flat): ActivityComment => c;
  const comments: CommentNode[] = tops.sort(byDate).map((t) => ({
    ...strip(t),
    replies: (repliesByParent.get(t.refId) ?? []).sort(byDate).map(strip),
  }));

  return { events, comments };
}
